import { FC, useMemo } from "react";
import { Stack, Box, Modal, Typography, styled, Grid } from "@mui/material";
import { IconClose } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { background, black, divider } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { useSharedTalentInfo } from "@/api/services/main/sharing";
import { convertResToFormData } from "@/features/talent/utils/convertTalentFormData";
import { TalentBasicInfo } from "@/features/talent/components/talent-detail/TalentBasicInfo";
import { TalentResidenceStatusInfo } from "@/features/talent/components/talent-detail/TalentResidenceStatusInfo";
import { SharedTalentLanguageAbility } from "@/features/sharing/components/SharedTalentLanguageAbility";
import { TalentEducationBackground } from "@/features/talent/components/talent-detail/TalentEducationBackground";
import { TalentWorkExperience } from "@/features/talent/components/talent-detail/TalentWorkExperience";
import { TalentMemo } from "@/features/talent/components/talent-detail/TalentMemo";
import { TalentInterviewEvaluation } from "@/features/talent/components/talent-detail/TalentInterviewEvaluation";
import { TalentFamilyInfo } from "@/features/talent/components/talent-detail/TalentFamilyInfo";
import { TalentRecruitmentStatus } from "@/features/talent/components/talent-detail/TalentRecruitmentStatus";
import { SharedTalentCertificateFiles } from "@/features/sharing/components/SharedTalentCertificateFiles";

type TSharedTalentInfoModalProps = {
  isOpen: boolean;
  talentId?: number;
  onClose: () => void;
  shareUrl: string;
};

/** Readonly talent info modal for shared (public) view */
export const SharedTalentInfoModal: FC<TSharedTalentInfoModalProps> = ({ isOpen, talentId, onClose, shareUrl }) => {
  const { data: talentData, isLoading } = useSharedTalentInfo(talentId, shareUrl);
  const talentFormData = useMemo(() => (talentData?.data ? convertResToFormData(talentData.data) : undefined), [talentData?.data]);

  const isLoadingContent = isOpen && talentId && (isLoading || !talentFormData);

  return (
    <Modal
      open={isOpen}
      hideBackdrop={false}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 0,
        "& .MuiBackdrop-root": {
          padding: 0,
        },
      }}
    >
      <ModalContainer>
        <Stack
          direction="row"
          bgcolor={background.disable}
          height="56px"
          minHeight="56px"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px", px: 3 }}
        >
          <Typography variant="body14Light" color={"#0B0C0D"}>
            人材情報
          </Typography>
          <IconBox onClick={onClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={24} />
          </IconBox>
        </Stack>

        <Box sx={{ flex: 1, overflowY: "auto", px: 4, pb: 4 }}>
          {isLoadingContent ? (
            <LoadingArea />
          ) : talentFormData ? (
            <Grid container spacing={4} justifyContent="center">
              <Grid item>
                <Box mt={4}>
                  <TalentRecruitmentStatus defaultValues={talentFormData} />
                </Box>
                <TalentBasicInfo defaultValues={talentFormData} isSharedView />
                <TalentResidenceStatusInfo defaultValues={talentFormData} isSharedView />
                <SharedTalentLanguageAbility defaultValues={talentFormData} />
                <TalentEducationBackground defaultValues={talentFormData} />
                <TalentWorkExperience defaultValues={talentFormData} />
                <SharedTalentCertificateFiles defaultValues={talentFormData} shareUrl={shareUrl} />
                <TalentMemo defaultValues={talentFormData} />
                <TalentInterviewEvaluation defaultValues={talentFormData} />
                <TalentFamilyInfo defaultValues={talentFormData} />
              </Grid>
            </Grid>
          ) : null}
        </Box>

        <Box>
          <hr color={divider.high} />
          <Stack direction="row" gap="16px" justifyContent="flex-end" p="16px 32px">
            <Button variant="ghost" size="sm" onClick={onClose} sx={{ width: "102px" }}>
              閉じる
            </Button>
          </Stack>
        </Box>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  width: 764px;
  height: 728px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  outline: none;
  position: relative;
  margin: auto;
  &:focus {
    outline: none;
  }
`;
